import { useParams } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, ArrowUpDown, Hammer } from "lucide-react";
import { useMemo, useState } from "react";
import { ConflictDetailSheet } from "@/components/conflicts/conflict-detail-sheet";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { type ConflictRow, useConflicts } from "@/hooks/use-conflicts";
import { cn } from "@/lib/utils";

const SKELETON_KEYS = ["a", "b", "c"] as const;

type SortKey = "id" | "status" | "date" | "description" | "parties";
type SortDir = "asc" | "desc";

export function ConflictsPage() {
  const { solutionId } = useParams({ from: "/s/$solutionId" });
  const { data, isLoading, isError, refetch } = useConflicts(solutionId);
  const [filter, setFilter] = useState<"open" | "resolved" | "all">("open");
  const [openId, setOpenId] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const filtered = useMemo(
    () => data?.filter((c) => filter === "all" || c.status === filter) ?? [],
    [data, filter],
  );

  const sorted = useMemo(
    () => sortConflicts(filtered, sortKey, sortDir),
    [filtered, sortKey, sortDir],
  );

  function toggleSort(k: SortKey) {
    if (sortKey === k) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(k);
      setSortDir("asc");
    }
  }

  return (
    <div className="mx-auto max-w-7xl px-6 py-8 space-y-6">
      <PageHeader
        title="Conflicts"
        description="Tracked disagreements between stakeholders. Capture positions, resolve, attach evidence."
      />

      <div className="rounded-lg border border-nymbl-blue/30 bg-nymbl-sky/60 px-4 py-3 flex items-start gap-3">
        <Hammer className="h-4 w-4 text-nymbl-navy mt-0.5 shrink-0" />
        <div className="flex-1 text-sm text-nymbl-navy">
          <span className="font-semibold">Coming soon.</span> Conflicts isn't ready for clients yet
          — what you see below is an early preview.
        </div>
      </div>

      <div className="flex gap-1.5">
        {(
          [
            { id: "open", label: "Open" },
            { id: "resolved", label: "Resolved" },
            { id: "all", label: "All" },
          ] as const
        ).map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={cn(
              "rounded-full px-3 py-1 text-xs font-medium transition-colors",
              filter === f.id
                ? "bg-nymbl-navy text-white"
                : "bg-muted text-foreground/70 hover:bg-muted/80",
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading && (
        <ul className="space-y-2">
          {SKELETON_KEYS.map((k) => (
            <Skeleton key={k} className="h-12 rounded-md" />
          ))}
        </ul>
      )}

      {isError && <ErrorState message="Failed to load conflicts" onRetry={() => refetch()} />}

      {data && sorted.length === 0 && !isLoading && (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] p-12 text-center">
          <p className="text-sm text-muted-foreground">No conflicts match this filter.</p>
        </div>
      )}

      {sorted.length > 0 && (
        <div className="rounded-xl border border-[var(--color-border)] bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold border-b border-[var(--color-border)]">
                  <Th className="w-10 text-right">#</Th>
                  <SortableTh
                    label="ID"
                    width="w-20"
                    active={sortKey === "id"}
                    dir={sortDir}
                    onClick={() => toggleSort("id")}
                  />
                  <SortableTh
                    label="Status"
                    width="w-24"
                    active={sortKey === "status"}
                    dir={sortDir}
                    onClick={() => toggleSort("status")}
                  />
                  <SortableTh
                    label="Date"
                    width="w-28"
                    active={sortKey === "date"}
                    dir={sortDir}
                    onClick={() => toggleSort("date")}
                  />
                  <SortableTh
                    label="Description"
                    active={sortKey === "description"}
                    dir={sortDir}
                    onClick={() => toggleSort("description")}
                  />
                  <SortableTh
                    label="Parties"
                    width="w-32"
                    active={sortKey === "parties"}
                    dir={sortDir}
                    onClick={() => toggleSort("parties")}
                  />
                  <Th className="w-[280px]">Resolution</Th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((c, i) => (
                  <tr
                    key={c.id}
                    onClick={() => setOpenId(c.id)}
                    className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-muted/40 cursor-pointer"
                  >
                    <Td className="text-right text-muted-foreground tabular-nums">{i + 1}</Td>
                    <Td className="font-mono text-[12px] tabular-nums">
                      {c.externalKey ?? c.id.slice(0, 6)}
                    </Td>
                    <Td>
                      <span
                        className={cn(
                          "inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold",
                          c.status === "resolved"
                            ? "bg-success-bg text-success"
                            : "bg-warning-bg text-warning",
                        )}
                      >
                        {c.status}
                      </span>
                    </Td>
                    <Td className="text-muted-foreground tabular-nums">
                      {new Date(c.conflictDate).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </Td>
                    <Td className="max-w-[420px]" title={c.description}>
                      <div className="truncate text-foreground/90">{c.description}</div>
                    </Td>
                    <Td>
                      <div className="flex -space-x-1.5">
                        {c.positions.slice(0, 4).map((p) => (
                          <span
                            key={p.id}
                            title={p.party ? `${p.party.name} — ${p.party.role}` : "Unknown"}
                            className={cn(
                              "inline-flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-semibold ring-2 ring-card",
                              p.party?.org === "client"
                                ? "bg-nymbl-sky text-nymbl-navy"
                                : "bg-nymbl-navy text-white",
                            )}
                          >
                            {p.party?.initials ?? "?"}
                          </span>
                        ))}
                        {c.positions.length === 0 && (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </div>
                    </Td>
                    <Td
                      className="max-w-[280px] text-muted-foreground"
                      title={c.resolutionNote ?? undefined}
                    >
                      <div className="truncate">{c.resolutionNote ?? "—"}</div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConflictDetailSheet
        conflictId={openId}
        solutionId={solutionId}
        onOpenChange={(open) => {
          if (!open) setOpenId(null);
        }}
      />
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={cn("text-left px-3 py-2.5 font-semibold whitespace-nowrap", className)}>
      {children}
    </th>
  );
}

function Td({
  children,
  className,
  title,
}: {
  children: React.ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <td
      className={cn("px-3 py-2 align-middle whitespace-nowrap overflow-hidden", className)}
      title={title}
    >
      {children}
    </td>
  );
}

function SortableTh({
  label,
  width,
  active,
  dir,
  onClick,
}: {
  label: string;
  width?: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
}) {
  return (
    <th className={cn("text-left px-3 py-2.5 font-semibold whitespace-nowrap select-none", width)}>
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1 group cursor-pointer hover:text-foreground"
      >
        <span>{label}</span>
        <span
          className={cn(
            "transition-opacity",
            active ? "opacity-100" : "opacity-30 group-hover:opacity-60",
          )}
        >
          {active && dir === "asc" ? (
            <ArrowUp className="h-3 w-3" />
          ) : active && dir === "desc" ? (
            <ArrowDown className="h-3 w-3" />
          ) : (
            <ArrowUpDown className="h-3 w-3" />
          )}
        </span>
      </button>
    </th>
  );
}

function sortConflicts(items: ConflictRow[], key: SortKey | null, dir: SortDir): ConflictRow[] {
  if (!key) return items;
  const sign = dir === "asc" ? 1 : -1;
  return [...items].sort((a, b) => {
    const va = sortValue(a, key);
    const vb = sortValue(b, key);
    if (typeof va === "number" && typeof vb === "number") return (va - vb) * sign;
    return String(va).localeCompare(String(vb)) * sign;
  });
}

function sortValue(c: ConflictRow, key: SortKey): string | number {
  switch (key) {
    case "id":
      return c.externalKey ?? c.id;
    case "status":
      return c.status;
    case "date":
      return new Date(c.conflictDate).getTime();
    case "description":
      return c.description;
    case "parties":
      return c.positions.length;
  }
}
